import * as React from 'react'

import { observer } from 'mobx-react'

import { useResourceFieldAllowances } from '~/client/src/shared/components/ResourceControls/useResourceFieldAllowances'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { toast } from '~/client/src/shared/utils/toast'

import { useSaveResourceFieldAllowances } from '../useSaveResourceFieldAllowances'
import WorkflowTypeFieldSelector from '../WorkflowTypeFieldSelector'
import EditableTagsCell from './EditableTagsCell'

interface IProps {
  isBookable: boolean
  /**
   * Called after an assignment lands on a resource that is not bookable. Assigning is what
   * the other controls treat as intent to enable, so the WR toggle follows the edit rather
   * than having to be flipped first.
   */
  onAssign?: (recordId: string) => void | Promise<void>
  /** The WR state on the server, where `isBookable` shows a draft of it (the resource card). */
  isSavedBookable?: boolean
  /** How the selector surfaces (popover vs full-panel sheet). */
  presentation?: 'popover' | 'sheet'
  /** The resource family whose fields the cell lists. */
  resourceTypeId: string
  /** The record whose allowance each field reflects. */
  recordId: string
}

function WorkflowTypesCell({
  isBookable,
  onAssign,
  isSavedBookable = isBookable,
  presentation,
  resourceTypeId,
  recordId,
}: IProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const groups = useResourceFieldAllowances(resourceTypeId, recordId)
  const saveAllowances = useSaveResourceFieldAllowances(recordId)

  // One chip per workflow type, naming the fields of it that allow this record -
  // "Material transfer: Delivered material, Spoiled material". A type whose every field
  // allows it reads "<type>: All" rather than listing them; a type with no allowed field
  // has nothing to say at all, so it gets no chip.
  const { chips, fieldCount, allowedCount } = React.useMemo(() => {
    const perType: {
      id: string
      typeName: string
      fieldNames: string
      iconName: string
      isInactive: boolean
      allowedFieldIds: string[]
    }[] = []
    let total = 0
    let allowed = 0

    groups.forEach(group => {
      const allowedFields = group.fields.filter(field => field.isAllowed)
      total += group.fields.length
      allowed += allowedFields.length

      if (!allowedFields.length) return

      perType.push({
        id: group.typeId,
        typeName: group.typeName,
        fieldNames:
          allowedFields.length === group.fields.length
            ? Localization.translator.all
            : allowedFields.map(field => field.label).join(', '),
        iconName: group.iconName,
        isInactive: group.isInactive,
        allowedFieldIds: allowedFields.map(field => field.fieldId),
      })
    })

    return { chips: perType, fieldCount: total, allowedCount: allowed }
  }, [groups])

  // Every field allowing this resource is the unrestricted state, and it is the common one -
  // naming them all would be a wall of text saying nothing. Collapsed to "All", matching what
  // this cell has always shown for an unrestricted record. No field allowing it is "None";
  // anything between names the fields, by type.
  const isUnrestricted = allowedCount === fieldCount
  const emptyLabel = isUnrestricted
    ? Localization.translator.all
    : Localization.translator.none

  return (
    <EditableTagsCell
      isBookable={isBookable}
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      cellAffordance="chevron"
      triggerLabel={Localization.translator.workflowTypes}
      presentation={presentation}
      popoverClassName="tw:w-[400px] tw:h-[min(560px,var(--available-height,85vh))]"
      emptyDisplay={
        <span className="tw:text-body-md tw:leading-[20px] tw:text-foreground">
          {emptyLabel}
        </span>
      }
      tags={
        isUnrestricted
          ? []
          : chips.map(chip => ({
              key: chip.id,
              // Greyed marks a type that is off in the Workflow Configurator: the
              // allowance stands, its workflow just can't be created right now.
              label: (
                <span
                  className={
                    chip.isInactive ? 'tw:text-muted-foreground' : undefined
                  }
                >
                  {chip.typeName}: {chip.fieldNames}
                </span>
              ),
              icon: (
                <WorkflowTypeIcon
                  iconName={chip.iconName}
                  size="small"
                  className={chip.isInactive ? 'tw:opacity-40' : ''}
                />
              ),
              // Dismissing a chip denies this record on every field of that type it is
              // currently allowed by - exactly the fields the chip names. Fields it is
              // already denied by are left alone rather than rewritten.
              onDismiss: () =>
                saveAllowances(
                  chip.allowedFieldIds.map(fieldId => ({
                    typeId: chip.id,
                    fieldId,
                    shouldAllow: false,
                  })),
                ).catch(() =>
                  toast.error(
                    Localization.translator.failedToSaveChangesTryAgain,
                  ),
                ),
            }))
      }
      popoverContent={
        // Edge-to-edge (no padding, Title/Actions live inside the selector's own
        // Root) so the selector owns its insets — mirrors PermittedCompaniesCell.
        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
          <WorkflowTypeFieldSelector
            resourceTypeId={resourceTypeId}
            recordId={recordId}
            title={Localization.translator.selectWorkflowTypes}
            onClose={() => setIsOpen(false)}
            className="tw:min-h-0 tw:flex-1"
            // WR off means the cascade has stripped this record's allowances, so an empty
            // list is not a choice the user made - open on "everything" and let them edit
            // down, the way the control behaved before the fields carried the state.
            selectAllByDefault={!isSavedBookable}
            onEnable={
              !isSavedBookable && onAssign
                ? async () => onAssign(recordId)
                : undefined
            }
          />
        </div>
      }
    />
  )
}

export default observer(WorkflowTypesCell)
